import { FC } from 'react'
import { PrivacyPolicyScreenContainer } from './style';

const PrivacyPolicySection: FC = () => {

    return (
        <PrivacyPolicyScreenContainer>
            <div className="background"></div>
            <div className="innerContainer">
                <div className="content">
                    <h1 className="title">Rook Hotel Privacy Policy</h1>
                    <p>
                        <b>Last Updated:</b> 14 Jan 2026<br />
                        <b>Hotel Name:</b> Rook Hotel<br />
                        <b>Address:</b> 3660 Hurontario St., Mississauga, Ontario, Canada<br />
                        <b>Contact Email:</b> privacy@rookhotel.com<br />
                    </p>
                    <p>
                        Rook Hotel (“we,” “our,” or “us”) is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website, make a reservation, stay at our hotel, or interact with us in any other way. <br />
                        By using our website or services, you agree to the terms of this Privacy Policy.
                    </p>

                    <h2 className="subtitle">1. Information We Collect</h2>
                    <h3 className="secondarySubtitle">1.1 Personal Information You Provide</h3>
                    <p>We may collect personal information when you:</p>
                    <ul>
                        <li>Make a reservation</li>
                        <li>Contact us by email, phone, or online form</li>
                        <li>Sign up for newsletters or promotional material</li>
                        <li>Check in or check out at the hotel</li>
                        <li>Use on-site services (e.g., Wi-Fi, amenities, events)</li>
                    </ul>
                    <p>This may include:</p>
                    <ul>
                        <li>Full name</li>
                        <li>Email address</li>
                        <li>Phone number</li>
                        <li>Address</li>
                        <li>Payment information (processed through secure third-party providers)</li>
                        <li>Reservation details</li>
                        <li>Government-issued ID (for check-in verification)</li>
                        <li>Preferences or special requests</li>
                    </ul>
                    <h3 className="secondarySubtitle">1.2 Automatically Collected Information</h3>
                    <p>When you use our website, we may automatically collect:</p>
                    <ul>
                        <li>IP address</li>
                        <li>Browser type and version</li>
                        <li>Device information</li>
                        <li>Usage data, such as pages visited, time spent, and referring URLs</li>
                        <li>Cookies, tracking pixels, and similar technologies</li>
                    </ul>
                    <h3 className="secondarySubtitle">1.3 Information from Third Parties</h3>
                    <p>We may receive information from:</p>
                    <ul>
                        <li>Online travel agencies (OTAs)</li>
                        <li>Booking engines</li>
                        <li>Payment processors</li>
                        <li>Marketing partners</li>
                    </ul>
                    <h2 className="subtitle">2. How We Use Your Information</h2>
                    <p>We use your information to:</p>
                    <ul>
                        <li>Process and manage reservations</li>
                        <li>Provide customer service</li>
                        <li>Communicate with you (confirmations, updates, promotions—only with consent)</li>
                        <li>Improve website performance and user experience</li>
                        <li>Improve website performance and user experience</li>
                        <li>Ensure security and prevent fraud</li>
                        <li>Comply with legal and regulatory requirements</li>
                    </ul>

                    <h2 className="subtitle">3. Legal Bases for Processing (for Guests from the EU/UK under GDPR)</h2>
                    <p>We process personal data under one or more of the following lawful bases:</p>
                    <ul>
                        <li><b>Contract:</b> To complete your reservation or stay.</li>
                        <li><b>Consent:</b> For marketing communications and optional data collection.</li>
                        <li><b>Legitimate Interests:</b> Security, fraud prevention, website analytics, service improvement.</li>
                        <li><b>Legal Obligation:</b> Compliance with local laws such as tax, public safety, or record-keeping requirements.</li>
                    </ul>

                    <h2 className="subtitle">4. How We Share Your Information</h2>
                    <p>We may disclose your information to:</p>
                    <ul>
                        <li><b>Service providers</b> such as booking engines, payment processors, email platforms, or IT providers</li>
                        <li><b>OTA partners</b> (e.g., Booking.com, Expedia) for reservation fulfillment</li>
                        <li><b>Regulatory authorities</b> if required by law</li>
                        <li><b>Security personnel</b> or authorities in emergency situations</li>
                        <li><b>Marketing partners</b> (only with your consent)</li>
                    </ul>
                    <p>We do <b>not</b> sell your personal information.</p>

                    <h2 className="subtitle">5. International Data Transfers</h2>
                    <p>If you are located outside Canada, your information may be transferred to servers in Canada or other jurisdictions. We ensure such transfers comply with applicable privacy laws through safeguards such as contractual clauses or secure processing agreements.</p>


                    <h2 className="subtitle">6. Data Retention</h2>
                    <p>We retain personal information only as long as necessary for:</p>
                    <ul>
                        <li>Fulfilling the purpose for which it was collected</li>
                        <li>Legal, tax, audit, or regulatory obligations</li>
                        <li>Legitimate business needs</li>
                    </ul>
                    <p>Booking and billing records are generally retained for <b>up to 7 years</b> as required by Canadian law.</p>

                    <h2 className="subtitle">7. Your Rights</h2>
                    <h3 className="secondarySubtitle">For All Guests (Canada – CPPA/PIPEDA Principles)</h3>
                    <p>You may request:</p>
                    <ul>
                        <li>Access to your personal information</li>
                        <li>Correction of inaccurate information</li>
                        <li>Withdrawal of marketing consent</li>
                        <li>Information about how your data is used</li>
                    </ul>

                    <h3 className="secondarySubtitle">For EU/UK Guests (GDPR Rights)</h3>
                    <p>You have the right to:</p>
                    <ul>
                        <li>Access your data</li>
                        <li>Request correction or deletion</li>
                        <li>Restrict or object to processing</li>
                        <li>Data portability</li>
                        <li>Withdraw consent at any time</li>
                        <li>Lodge a complaint with a supervisory authority</li>
                    </ul>
                    <p>Requests can be made at: <a className='mailTo' href='mailto: privacy@rookhotel.com'>privacy@rookhotel.com</a></p>
                    <p>We may require verification of identity before fulfilling any request.</p>


                    <h2 className="subtitle">8. Cookies and Tracking Technologies</h2>
                    <p>Our website uses cookies and similar technologies to:</p>
                    <ul>
                        <li>Provide core site functionality</li>
                        <li>Improve site performance</li>
                        <li>Analyze visitor usage</li>
                        <li>Deliver personalized content or marketing (only with consent where required)</li>
                    </ul>
                    <p>You can manage cookie preferences through your browser settings or our on-site cookie banner.</p>

                    <h2 className="subtitle">9. Security of Your Information</h2>
                    <p>We implement administrative, technical, and physical safeguards to protect your data, including:</p>
                    <ul>
                        <li>Secure servers</li>
                        <li>Encrypted connections (HTTPS)</li>
                        <li>Restricted access to personal data</li>
                        <li>Staff training and confidentiality requirements</li>
                    </ul>
                    <p>No system is 100% secure, but we take reasonable steps to minimize risk.</p>

                    <h2 className="subtitle">10. Third-Party Links</h2>
                    <p>Our website may contain links to third-party sites (e.g., OTAs, payment platforms). We are not responsible for their privacy practices. We encourage you to review their privacy policies before providing personal information.</p>

                    <h2 className="subtitle">11. Children’s Privacy</h2>
                    <p>Our services are not directed to children under 16. We do not knowingly collect personal information from minors without parental consent. If we learn we have inadvertently collected such data, we will delete it promptly.</p>

                    <h2 className="subtitle">12. Updates to This Privacy Policy</h2>
                    <p>We may update this Privacy Policy from time to time. The “Last Updated” date at the top will reflect the latest revision. Continued use of our website or services constitutes acceptance of the updated policy.</p>

                    <h2 className="subtitle">13. Contact Us</h2>
                    <p>If you have questions, concerns, or data protection requests, contact us at:</p>

                    <p>
                        <b>Rook Hotel</b><br />
                        3660 Hurontario St.<br />
                        Mississauga, Ontario, Canada<br />
                        <b>Email:</b> <a href="mailto:privacy@rookhotel.com" className='mailToBut'>privacy@rookhotel.com</a><br />
                    </p>

                </div>
            </div>
        </PrivacyPolicyScreenContainer>
    )
}

export default PrivacyPolicySection;